import {
  isClosedRegistrationPtmApplication,
  isDeniedPtmApplication,
  isDraftPtmApplication,
  shouldReplaceApplyCtaWithStatus,
} from "./prospect/ptmApplicationStatus"

/**
 * Resume a leftover draft when Coach Tools reports the membership path is still
 * valid. `pathEligible` ignores in-progress applications and submit prereqs
 * (BGC, PAT, QL, dues, age). Missing/false fails closed.
 *
 * @param {boolean | null | undefined} pathEligible
 * @returns {boolean}
 */
export function canResumeDraftApplication(pathEligible) {
  return pathEligible === true
}

/**
 * Presentation paths for Associate/Affiliate reapply, driven by Coach Tools
 * eligibility + latest PTM application status. Shared by TerminatedModal,
 * Reapply Path Offer surfaces, and Course Curriculum Become/Resume Associate.
 */
export const REAPPLY_PATH = {
  APPLY: "apply",
  RESUME: "resume",
  STATUS: "status",
  DENIED: "denied",
  CLOSED: "closed",
  UNAVAILABLE: "unavailable",
}

/**
 * Per-path action state (Associate or Affiliate).
 * Denied keeps Apply when canStart is true — backend does not treat denied as blocking.
 * Closed unpaid registration keeps Apply (Reapply label) without a status section.
 * Draft shows Resume when `pathEligible === true`. Leftover drafts
 * (`pathEligible` false/missing) are unavailable (no Resume, no Become).
 *
 * @param {{ status?: string, registrationStatus?: string | null } | null | undefined} application
 * @param {boolean} canStart - Become / Reapply when there is no leftover draft
 * @param {boolean | null | undefined} [pathEligible] - Coach Tools membership-path gate
 */
export function reapplyPathActionState(application, canStart, pathEligible) {
  const replaceApplyCta = shouldReplaceApplyCtaWithStatus(
    application?.status,
    application?.registrationStatus,
  )
  const showDeniedStatus = isDeniedPtmApplication(application?.status)
  const isClosedRegistration = isClosedRegistrationPtmApplication(
    application?.status,
    application?.registrationStatus,
  )
  const isDraft = isDraftPtmApplication(application?.status)
  const isResume = isDraft && canResumeDraftApplication(pathEligible)
  const leftoverDraft = isDraft && !isResume
  const showApplyCta =
    !replaceApplyCta && !leftoverDraft && (isResume || canStart === true)
  const showStatus = replaceApplyCta || showDeniedStatus

  let path = REAPPLY_PATH.UNAVAILABLE
  if (replaceApplyCta) {
    path = REAPPLY_PATH.STATUS
  } else if (showDeniedStatus) {
    path = REAPPLY_PATH.DENIED
  } else if (isClosedRegistration) {
    path = REAPPLY_PATH.CLOSED
  } else if (leftoverDraft) {
    path = REAPPLY_PATH.UNAVAILABLE
  } else if (isResume) {
    path = REAPPLY_PATH.RESUME
  } else if (showApplyCta) {
    path = REAPPLY_PATH.APPLY
  }

  return {
    path,
    showApplyCta,
    showStatus,
    showDeniedStatus,
    isClosedRegistration,
    replaceApplyCta,
    isResume,
  }
}

/**
 * Combined Associate + Affiliate offer for Reapply Path Offer consumers.
 *
 * @param {object} input
 * @param {boolean} input.associateEligible
 * @param {boolean} input.affiliateEligible
 * @param {object | null} [input.associateApplication]
 * @param {object | null} [input.affiliateApplication]
 * @param {boolean | null} [input.associatePathEligible]
 * @param {boolean | null} [input.affiliatePathEligible]
 * @param {boolean} [input.applicationsReady=true]
 * @param {boolean} [input.eligibilityReady=true]
 */
export function buildReapplyPathOffer({
  associateEligible,
  affiliateEligible,
  associateApplication = null,
  affiliateApplication = null,
  associatePathEligible,
  affiliatePathEligible,
  applicationsReady = true,
  eligibilityReady = true,
}) {
  if (!applicationsReady || !eligibilityReady) {
    return {
      associate: null,
      affiliate: null,
      showAnyApplyCta: false,
      showAnyStatus: false,
      isUnavailable: false,
    }
  }

  const associate = reapplyPathActionState(
    associateApplication,
    associateEligible,
    associatePathEligible,
  )
  const affiliate = reapplyPathActionState(
    affiliateApplication,
    affiliateEligible,
    affiliatePathEligible,
  )
  const showAnyApplyCta = associate.showApplyCta || affiliate.showApplyCta
  const showAnyStatus = associate.showStatus || affiliate.showStatus

  return {
    associate,
    affiliate,
    showAnyApplyCta,
    showAnyStatus,
    isUnavailable: !showAnyApplyCta && !showAnyStatus,
  }
}

/**
 * Whether to load Coach Tools eligibility + PTM applications.
 * Skip only explicit Members (MB); Coach Tools also blocks Members server-side.
 */
export function shouldFetchReapplyPathEligibility({ programType }) {
  return programType !== "MB"
}

/**
 * Settled Coach Tools eligibility result for one path.
 */
export function isReapplyPathEligible({
  shouldFetch,
  loading,
  error,
  eligible,
}) {
  return !!shouldFetch && !loading && !error && eligible === true
}

/**
 * Live program type first; fall back to type-at-termination when auth `me`
 * omits it. AF normalizes to AP for Member-skip checks.
 */
export function resolvedProgramTypeForReapply(me) {
  const liveType = me?.mainProgramTypeCode || me?.type
  if (liveType) return liveType === "AF" ? "AP" : liveType

  const terminationType = me?.termination?.type
  return terminationType === "AF" ? "AP" : terminationType
}

/**
 * PTM applications query settled (or skipped).
 */
export function areReapplyPathApplicationsReady({
  shouldFetch,
  loading,
  data,
  error,
}) {
  return !shouldFetch || (!loading && !!(data || error))
}

/**
 * Eligibility query settled (or skipped). Same contract as applications.
 */
export function areReapplyPathEligibilityReady({
  shouldFetch,
  loading,
  data,
  error,
}) {
  return areReapplyPathApplicationsReady({
    shouldFetch,
    loading,
    data,
    error,
  })
}
